// tabs/ThinkyBaaSSecurity.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldAlt, faLock, faCheckCircle, faPlay, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

// Dummy Security & Privacy data
const dummySecurityMeasures = [
  {
    id: 1,
    name: "Encryption at Rest & Transit",
    description: "Ensure all blockchain data is encrypted both at rest and during transit.",
    enabled: false,
    tested: false,
    verified: false,
    log: [],
    precondition: true,
  },
  {
    id: 2,
    name: "Smart Contract Vulnerability Scan",
    description: "Detects potential vulnerabilities in deployed smart contracts.",
    enabled: false,
    tested: false,
    verified: false,
    log: [],
    precondition: false, // simulate scan blocked
  },
  {
    id: 3,
    name: "User Data Privacy Compliance",
    description: "Ensures GDPR/HIPAA compliance for all user and IoT data.",
    enabled: false,
    tested: false,
    verified: false,
    log: [],
    precondition: true,
  },
];

const ThinkyBaaSSecurity = () => {
  const [measures, setMeasures] = useState(dummySecurityMeasures.map(m => ({ ...m, log: m.log || [] })));

  // Enable security
  const enableSecurity = (id) => {
    setMeasures(prev =>
      prev.map(m =>
        m.id === id && !m.enabled
          ? { ...m, enabled: true, log: [...(m.log || []), { event: "Security Enabled", time: new Date().toLocaleTimeString() }] }
          : m
      )
    );
  };

  // Test security
  const testSecurity = (id) => {
    setMeasures(prev =>
      prev.map(m => {
        if (m.id === id) {
          if (!m.precondition) {
            return { ...m, log: [...(m.log || []), { event: "Security Test blocked: precondition not met", time: new Date().toLocaleTimeString() }] };
          }
          return { ...m, tested: true, log: [...(m.log || []), { event: "Security Tested", time: new Date().toLocaleTimeString() }] };
        }
        return m;
      })
    );
  };

  // Verify security
  const verifySecurity = (id) => {
    setMeasures(prev =>
      prev.map(m =>
        m.id === id && m.tested && !m.verified
          ? { ...m, verified: true, log: [...(m.log || []), { event: "Security Verified ✅", time: new Date().toLocaleTimeString() }] }
          : m
      )
    );
  };

  return (
    <div className="bg-gray-900 p-6 rounded-2xl shadow-lg space-y-6">
      <div className="flex items-center mb-4">
        <FontAwesomeIcon icon={faShieldAlt} className="text-blue-400 text-xl lg:text-2xl mr-4" />
        <h2 className="text-lg lg:text-xl font-bold">Security & Privacy Workflow</h2>
      </div>

      <p className="text-gray-300 text-sm">
        Simulated <span className="font-semibold">Security & Privacy workflow</span>:
        Enable → Test → Verify. Event logs ensure transparency and traceability.
      </p>

      <div className="space-y-4">
        {measures.map(m => (
          <div key={m.id} className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <h3 className="text-xl font-semibold mb-1">{m.name}</h3>
            <p className="text-gray-400 mb-2">{m.description}</p>

            <div className="flex flex-wrap gap-2 mb-2">
              <span className={`px-2 py-1 rounded-sm text-sm ${m.enabled ? "bg-green-600" : "bg-yellow-600"}`}>
                {m.enabled ? "Enabled ✅" : "Pending Enable ⏳"}
              </span>
              <span className={`px-2 py-1 rounded-sm text-sm ${m.tested ? "bg-green-600" : "bg-yellow-600"}`}>
                {m.tested ? "Tested ✅" : "Pending Test ⏳"}
              </span>
              <span className={`px-2 py-1 rounded-sm text-sm ${m.verified ? "bg-green-600" : "bg-yellow-600"}`}>
                {m.verified ? "Verified ✅" : "Pending Verification ⏳"}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                disabled={m.enabled}
                onClick={() => enableSecurity(m.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  m.enabled ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faLock} className="mr-1" /> Enable
              </button>
              <button
                disabled={!m.enabled || m.tested}
                onClick={() => testSecurity(m.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  !m.enabled || m.tested ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faPlay} className="mr-1" /> Test
              </button>
              <button
                disabled={!m.tested || m.verified}
                onClick={() => verifySecurity(m.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  !m.tested || m.verified ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Verify
              </button>
            </div>

            <div className="mt-3 bg-gray-900 p-2 rounded-lg border border-gray-700">
              <h4 className="text-gray-300 font-semibold mb-1">Event Log:</h4>
              {(!m.log || m.log.length === 0) ? (
                <p className="text-gray-500 text-sm">No events yet.</p>
              ) : (
                <ul className="text-gray-400 text-sm list-disc list-inside space-y-1">
                  {m.log?.map((log, idx) => (
                    <li key={idx}>
                      [{log.time}] {log.event}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThinkyBaaSSecurity;
